import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { outputSurface } from '../../theme';
import { Board, ParseResult, addParsed, fetchBoard, parseList } from '../../lib/plan';
import { useElapsed } from './useElapsed';

/**
 * A pasted list becomes candidates — whether it was a day-by-day itinerary or
 * five must-sees. Putting them on days is a separate, later step; a pasted day
 * order is offered only as an opt-in first draft, and lands as suggestions.
 */
export default function PastePage() {
  const { tripId } = useParams<{ tripId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const initialText = (location.state as { text?: string } | null)?.text ?? '';

  const [board, setBoard] = useState<Board | null>(null);
  const [text, setText] = useState(initialText);
  const [running, setRunning] = useState(false);
  const elapsed = useElapsed(running);
  const [result, setResult] = useState<ParseResult | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [stars, setStars] = useState<Set<number>>(new Set());
  const [useDayOrder, setUseDayOrder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoRan = useRef(false);

  useEffect(() => {
    if (!tripId) return;
    fetchBoard(tripId).then(({ data, error: err }) => {
      setBoard(data);
      if (err) setError(err);
    });
  }, [tripId]);

  const run = useCallback(async () => {
    if (!tripId || !text.trim()) return;
    setError(null);
    setResult(null);
    setRunning(true);
    const { data, error: err } = await parseList(tripId, text.trim());
    setRunning(false);
    if (err || !data) {
      setError(err ?? 'Nothing came back. Try again.');
      return;
    }
    setResult(data);
    setPicked(new Set(data.candidates.map((_, i) => i)));
    setStars(new Set(data.candidates.flatMap((c, i) => (c.must_see ? [i] : []))));
  }, [tripId, text]);

  // Arriving from New trip with a list: read it straight away.
  useEffect(() => {
    if (initialText && !autoRan.current) {
      autoRan.current = true;
      run();
    }
  }, [initialText, run]);

  const groups = useMemo(() => {
    const out = new Map<string, number[]>();
    (result?.candidates ?? []).forEach((c, i) => {
      const town = c.city ?? 'Somewhere';
      out.set(town, [...(out.get(town) ?? []), i]);
    });
    return Array.from(out.entries());
  }, [result]);

  async function add() {
    if (!board || !result) return;
    setSaving(true);
    const chosen = result.candidates
      .map((c, i) => ({ ...c, must_see: stars.has(i) }))
      .filter((_, i) => picked.has(i));
    const { error: err } = await addParsed(board, chosen, {
      useDayOrder: useDayOrder && result.has_day_order,
      days: result.days,
      hints: result.travel_hints,
      notes: result.notes,
    });
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    navigate(`/trips/${tripId}`, { replace: true });
  }

  const toggle = (set: Set<number>, i: number) => {
    const next = new Set(set);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    return next;
  };

  return (
    <Box>
      <Button component={RouterLink} to={`/trips/${tripId}`} size="small" sx={{ mb: 1, ml: -1 }}>
        ← {board?.trip.title ?? 'Back to the trip'}
      </Button>

      {!result ? (
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            Paste a list
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            A day-by-day plan from ChatGPT, a note, an email, or just your must-sees. Roamoir pulls out the places as
            candidates for you to check.
          </Typography>
          <TextField
            multiline
            minRows={12}
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={running}
            placeholder={'Uffizi\nBrancacci Chapel\nSiena Cathedral\nA hike in the Val d’Orcia\n…'}
            slotProps={{ input: { sx: { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '0.85rem' } } }}
          />
          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={run} disabled={running || !text.trim()}>
              {running ? <CircularProgress size={20} color="inherit" /> : 'Read my list'}
            </Button>
            {running ? (
              <Typography variant="body2" color="text.secondary">
                Reading… {elapsed}s
              </Typography>
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            {result.candidates.length} {result.candidates.length === 1 ? 'candidate' : 'candidates'} from your list
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
            These join your candidates first. Star the ones the trip is built around and untick any you’ve gone off.
            Putting them on days comes after.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '999 1 600px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, py: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>Candidates by town</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {picked.size} of {result.candidates.length} selected · {stars.size} must-see
                  </Typography>
                </Box>
                {groups.map(([town, idx]) => (
                  <Box key={town} sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2.5, py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.25 }}>
                      <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{town}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {idx.length} {idx.length === 1 ? 'place' : 'places'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                      {idx.map((i) => {
                        const c = result.candidates[i];
                        const on = picked.has(i);
                        return (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              minHeight: 48,
                              pl: 0.5,
                              pr: 0.5,
                              border: '1px solid',
                              borderColor: on ? 'primary.light' : 'divider',
                              borderRadius: 1.5,
                              opacity: on ? 1 : 0.55,
                            }}
                          >
                            <Checkbox size="small" checked={on} onChange={() => setPicked((p) => toggle(p, i))} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {c.icon} {c.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[c.hours ? `~${c.hours} h` : null, c.note, c.day_number ? `Day ${c.day_number} in your list` : null]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </Typography>
                            </Box>
                            <Tooltip title={stars.has(i) ? 'Must-see' : 'Mark as must-see'}>
                              <IconButton size="small" onClick={() => setStars((s) => toggle(s, i))}>
                                {stars.has(i) ? (
                                  <StarIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                                ) : (
                                  <StarBorderIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Paper>

              {result.travel_hints.length || result.notes.length ? (
                <Paper variant="outlined" sx={{ px: 2.5, py: 2 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>Also in your list</Typography>
                  {result.travel_hints.length ? (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Travel hints:</strong> {result.travel_hints.map((h) => `“${h}”`).join(', ')}. Kept for when
                      you plan the days; they don’t decide anything yet.
                    </Typography>
                  ) : null}
                  {result.notes.length ? (
                    <Typography variant="body2">
                      <strong>Notes:</strong> {result.notes.join('; ')}. Added to the trip’s notes.
                    </Typography>
                  ) : null}
                </Paper>
              ) : null}
            </Box>

            <Paper variant="outlined" sx={{ flex: '1 1 300px', minWidth: 0, p: 2.5 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Add to candidates</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {picked.size} candidates, {Array.from(picked).filter((i) => stars.has(i)).length} starred.
              </Typography>
              {result.has_day_order ? (
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.default', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Your list also has a day-by-day order
                  </Typography>
                  <FormControlLabel
                    control={<Checkbox checked={useDayOrder} onChange={(e) => setUseDayOrder(e.target.checked)} />}
                    label={<Typography variant="body2">Use it as a first draft of the days</Typography>}
                  />
                  {useDayOrder && result.days?.length ? (
                    <Box component="ol" sx={{ listStyle: 'none', m: 0, mb: 1, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {result.days.map((d) => (
                        <Box component="li" key={d.day_number} sx={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Day {d.day_number}
                          </Typography>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {d.label ?? '—'}
                            </Typography>
                            {d.sleep ? (
                              <Typography variant="caption" color="text.secondary">
                                Sleep in {d.sleep}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Places appear on those days as suggestions you keep or drop, each day gets its heading as a label, and
                    the town you sleep in fills any night not yet decided. Leave it off to plan the days with Roamoir instead.
                  </Typography>
                </Box>
              ) : null}
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : null}
              <Button variant="contained" fullWidth size="large" onClick={add} disabled={saving || picked.size === 0 || !board}>
                {saving ? <CircularProgress size={22} color="inherit" /> : `Add ${picked.size} to candidates`}
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={() => setResult(null)} disabled={saving}>
                Edit the list
              </Button>
              <Box sx={{ mt: 2, p: 2, borderRadius: 1.5, bgcolor: outputSurface.bg, color: outputSurface.muted }}>
                <Typography variant="body2">
                  Works with any list: a full itinerary, a few lines of must-sees, or names copied from an article.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
}
